import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { BtnLoader } from "src/Loader";
import { useAuth } from "src/contexts/auth-provider";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { financeUrl, type FinanceDispute } from "./financeApi";

const statusClasses: Record<string, string> = {
  NEEDS_RESPONSE: "bg-amber-50 text-amber-800 border-amber-200",
  INQUIRY: "bg-amber-50 text-amber-800 border-amber-200",
  PENDING: "bg-blue-50 text-blue-700 border-blue-200",
  UNDER_REVIEW: "bg-blue-50 text-blue-700 border-blue-200",
  WON: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LOST: "bg-red-50 text-red-700 border-red-200",
  ACCEPTED: "bg-slate-100 text-slate-700 border-slate-200",
};

export function FinanceDisputesPage() {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const { redirectTo } = useRestaurantFromParams();
  const [disputes, setDisputes] = useState<FinanceDispute[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<FinanceDispute | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storeCorrelationId = hasRestaurant?.correlationId;

  const selectedDisputeId = selectedId ?? disputes[0]?.correlationId ?? null;

  useEffect(() => {
    if (!storeCorrelationId) return;
    setLoading(true);
    setError(null);
    axBe
      .get<FinanceDispute[]>(financeUrl("disputes", storeCorrelationId))
      .then((response) => {
        const rows = response.data ?? [];
        setDisputes(rows);
        setSelectedId((current) => current ?? rows[0]?.correlationId ?? null);
      })
      .catch(() => setError("Unable to load disputes."))
      .finally(() => setLoading(false));
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    if (!storeCorrelationId || !selectedDisputeId) {
      setSelected(null);
      return;
    }
    setDetailLoading(true);
    axBe
      .get<FinanceDispute>(financeUrl(`disputes/${selectedDisputeId}`, storeCorrelationId))
      .then((response) => setSelected(response.data))
      .catch(() => setError("Unable to load dispute details."))
      .finally(() => setDetailLoading(false));
  }, [axBe, selectedDisputeId, storeCorrelationId]);

  const selectedRow = useMemo(
    () => disputes.find((dispute) => dispute.correlationId === selectedDisputeId) ?? null,
    [disputes, selectedDisputeId],
  );

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <ReportPageLayout hideTitle error={error ?? undefined}>
      <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
        <div className="w-full overflow-hidden rounded-xl border border-[#F1F1F2] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
          {loading ? (
            <div className="flex min-h-[260px] items-center justify-center"><BtnLoader /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-left text-sm">
                <thead className="bg-[#f8fafc] text-xs font-semibold uppercase text-[#475569]">
                  <tr>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Respond By</th>
                    <th className="px-4 py-3">Dispute ID</th>
                  </tr>
                </thead>
                <tbody>
                  {disputes.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6b7280]">No disputes.</td></tr>
                  ) : disputes.map((dispute) => (
                    <tr
                      key={dispute.correlationId ?? dispute.disputeId ?? dispute.createdAt}
                      className={`cursor-pointer border-t border-[#e5e7eb] ${selectedDisputeId === dispute.correlationId ? "bg-[#fff7f3]" : "hover:bg-[#fafafa]"}`}
                      onClick={() => setSelectedId(dispute.correlationId ?? null)}
                    >
                      <td className="px-4 py-3"><StatusBadge status={dispute.status} /></td>
                      <td className="px-4 py-3">{dispute.reason ?? "-"}</td>
                      <td className="px-4 py-3 font-semibold">{formatCentsToDollarsWithDollarSign(dispute.amountCents ?? 0)}</td>
                      <td className="px-4 py-3">{formatDate(dispute.respondBy)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{dispute.disputeId ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="rounded-xl border border-[#F1F1F2] bg-white p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
          {detailLoading ? (
            <div className="flex min-h-[260px] items-center justify-center"><BtnLoader /></div>
          ) : selected || selectedRow ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3 border-b border-[#e5e7eb] pb-4">
                <div>
                  <h2 className="text-lg font-semibold text-[#111827]">Dispute detail</h2>
                  <p className="mt-1 font-mono text-xs text-[#6b7280]">{(selected ?? selectedRow)?.disputeId}</p>
                </div>
                <StatusBadge status={(selected ?? selectedRow)?.status} />
              </div>

              <div className="rounded-lg border border-[#dbeafe] bg-[#eff6ff] px-3 py-3 text-sm text-[#1e3a8a]">
                EatsBits is handling this dispute response because this enterprise order was delivered by the EatsBits platform.
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Detail label="Amount" value={formatCentsToDollarsWithDollarSign((selected ?? selectedRow)?.amountCents ?? 0)} />
                <Detail label="Reason" value={(selected ?? selectedRow)?.reason ?? "-"} />
                <Detail label="Respond by" value={formatDate((selected ?? selectedRow)?.respondBy)} />
                <Detail label="Order" value={(selected ?? selectedRow)?.customerOrderCorrelationId ? `#${(selected ?? selectedRow)?.customerOrderCorrelationId?.slice(-6).toUpperCase()}` : "-"} />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-[#111827]">Evidence</h3>
                <div className="space-y-2">
                  {(selected?.evidence ?? []).length === 0 ? (
                    <div className="rounded-lg border border-dashed border-[#d1d5db] px-3 py-4 text-sm text-[#6b7280]">No evidence has been added yet.</div>
                  ) : selected?.evidence?.map((item) => (
                    <div key={item.correlationId ?? item.finixEvidenceId} className="rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm">
                      <div className="font-medium text-[#111827]">{item.fileName ?? item.evidenceType ?? "Evidence"}</div>
                      <div className="mt-1 text-xs text-[#6b7280]">{item.evidenceType ?? "OTHER"} · {item.submittedToFinix ? "Submitted" : "Draft"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[260px] items-center justify-center text-sm text-[#6b7280]">Select a dispute.</div>
          )}
        </aside>
      </div>
    </ReportPageLayout>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  const key = (status ?? "UNKNOWN").toUpperCase();
  const className = statusClasses[key] ?? "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}>{key.replace(/_/g, " ")}</span>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[#f8fafc] px-3 py-2">
      <div className="text-xs font-medium text-[#6b7280]">{label}</div>
      <div className="mt-1 break-words text-sm font-semibold text-[#111827]">{value}</div>
    </div>
  );
}

function toDate(value?: string | number | null) {
  if (!value) return null;
  if (typeof value === "number") {
    const millis = Math.abs(value) < 100_000_000_000 ? value * 1000 : value;
    return new Date(millis);
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && value.trim() !== "") {
    const millis = Math.abs(numeric) < 100_000_000_000 ? numeric * 1000 : numeric;
    return new Date(millis);
  }
  return new Date(value);
}

function formatDate(value?: string | number | null) {
  if (!value) return "-";
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
}
